import { type HtmlElement, Node } from "../dom";
import { type TagEndEvent } from "../event";
import { type RuleDocumentation, type SchemaObject, Rule, ruleDocumentationUrl } from "../rule";

type RuleContext = string;

interface RuleOptions {
	ignoreForeign: boolean;
	ignoreXML: boolean;
}

const xmlns = /^[^:]+:.+$/;
const defaults: RuleOptions = {
	ignoreForeign: true,
	ignoreXML: true,
};

export default class NoSelfClosing extends Rule<RuleContext, RuleOptions> {
	public constructor(options: Partial<RuleOptions>) {
		super({ ...defaults, ...options });
	}

	public static override schema(): SchemaObject {
		return {
			ignoreForeign: {
				type: "boolean",
			},
			ignoreXML: {
				type: "boolean",
			},
		};
	}

	public override documentation(context: RuleContext): RuleDocumentation {
		return {
			description: `Self-closing elements are disallowed. Use regular end tag <${context}></${context}> instead of self-closing <${context}/>.`,
			url: ruleDocumentationUrl(__filename),
		};
	}

	public setup(): void {
		this.on("tag:end", (event: TagEndEvent) => {
			const active = event.previous; // The current active element (that is, the current element on the stack)

			if (!isRelevant(active, this.options)) {
				return;
			}

			this.validateElement(active);
		});
	}

	private validateElement(node: HtmlElement): void {
		if (node.closed !== Node.CLOSED_VOID_SELF_CLOSED) {
			return;
		}

		this.report(node, `<${node.tagName}> must not be self-closed`, null, node.tagName);
	}
}

function isRelevant(node: HtmlElement, options: RuleOptions): boolean {
	/* tags in XML namespaces are relevant only if ignoreXml is false, in which
	 * case assume all XML elements must not be self-closed */
	if (xmlns.test(node.tagName)) {
		return !options.ignoreXML;
	}

	/* nodes with missing metadata is assumed relevant */
	if (!node.meta) {
		return true;
	}

	if (node.meta.void) {
		return false;
	}

	/* foreign elements are relevant only if ignoreForeign is false, in which case
	 * assume all foreign must not be self-closed */
	if (node.meta.foreign) {
		return !options.ignoreForeign;
	}

	return true;
}
